import React, { useCallback, useEffect, useState } from 'react'

import ConsoleFooter from './ConsoleFooter'
import ConsoleDialog from './ConsoleDialog'
import DataTable from './DataTable'
import { mapStakingError } from './errorMessages'
import { formatIntegerAmount, formatTokenAmount } from './formatAmount'
import PoolOverview from './PoolOverview'
import ReferralPanel from './ReferralPanel'
import RewardWithdrawalPanel from './RewardWithdrawalPanel'
import NftSelectionDialog from './NftSelectionDialog'
import NftPagedList from './NftPagedList'
import { getRewardWithdrawalProtection } from './stakingLimits'

const tokenIdOf = (item) => String(typeof item === 'object' ? item.id : item)
const formatPoolDate = (value) => value?.format?.('YYYY-MM-DD HH:mm') || '-'

export default function NftStakingConsole({ controller }) {
    const [selectionMode, setSelectionMode] = useState(null)
    const [dangerAction, setDangerAction] = useState(null)
    const [dangerLoading, setDangerLoading] = useState(false)
    const [dangerError, setDangerError] = useState('')
    const [nowUnix, setNowUnix] = useState(() => Math.floor(Date.now() / 1000))
    const [initialLoadTimedOut, setInitialLoadTimedOut] = useState(false)
    const [initialLoadAttempt, setInitialLoadAttempt] = useState(0)
    const state = controller.state || {}
    const t = controller.props?.t || ((key) => key)
    const { poolInfo = {}, stakeTokenInfo = {}, rewardTokenInfo = {}, countdown = {} } = state
    const account = state.account || controller.props?.account || ''
    const walletNfts = Array.isArray(state.walletNfts) ? state.walletNfts : []
    const manualNfts = Array.isArray(state.manualNfts) ? state.manualNfts : []
    const availableNfts = [...walletNfts, ...manualNfts]
    const stakedNftIds = Array.isArray(state.stakedNftIds) ? state.stakedNftIds : []
    const nftMetadataById = state.nftMetadataById || {}
    const poolLoaded = Object.keys(poolInfo).length > 0
    const poolDataLoading = Boolean(state.poolDataLoading)
    const poolDataError = state.poolDataError || ''
    const poolDataErrorKind = state.poolDataErrorKind || ''
    const walletNftsLoading = state.walletNfts === undefined
    const stakedNftsLoading = Boolean(state.stakedNftsLoading)
    const essentialDataReady = poolLoaded && !poolDataLoading && !poolDataError
    const isOwner = Boolean(account && (
        state.isAdmin
        || (poolInfo.owner && poolInfo.owner.toLowerCase() === account.toLowerCase())
    ))
    const isEnded = countdown.type === 3
    const endTimeUnix = poolInfo.endTime?.unix?.() || 0
    const rewardClaimAudit = state.rewardClaimAudit || {}
    const rewardWithdrawalProtection = getRewardWithdrawalProtection({
        endTimeUnix,
        nowUnix,
        canWithdrawEarly: rewardClaimAudit.canWithdrawEarly,
    })
    const countdownLoading = !poolLoaded || countdown.type === 0
    const countdownLabel = countdownLoading
        ? t('加载中')
        : countdown.type === 1
            ? t('即将开始')
            : isEnded
                ? t('已结束')
                : t('结束倒计时')
    const adminInfo = state.adminInfo || {}
    const loadVisibleNftMetadata = useCallback((tokenIds) => {
        controller.loadNftMetadataForIds?.(tokenIds)
    }, [controller])
    const askDangerAction = (action) => {
        setDangerError('')
        setDangerAction(action)
    }
    const confirmDangerAction = async () => {
        if (!dangerAction || dangerLoading) return
        setDangerLoading(true)
        setDangerError('')
        try {
            const result = await dangerAction.run()
            if (result === false) throw new Error(t('交易失败，请稍后重试'))
            setDangerAction(null)
            if (result && typeof result === 'object') {
                controller.showTransactionSuccess?.(result)
            }
        } catch (error) {
            setDangerError(mapStakingError(error, t))
        } finally {
            setDangerLoading(false)
        }
    }

    useEffect(() => {
        setSelectionMode(null)
    }, [account, state.chainId])

    useEffect(() => {
        if (essentialDataReady) {
            setInitialLoadTimedOut(false)
            return undefined
        }
        setInitialLoadTimedOut(false)
        const timer = window.setTimeout(() => setInitialLoadTimedOut(true), 15000)
        return () => window.clearTimeout(timer)
    }, [essentialDataReady, initialLoadAttempt, state.chainId, state.contract])

    useEffect(() => {
        if (!isOwner || !isEnded || !endTimeUnix || rewardWithdrawalProtection.isAvailable) return undefined
        const timer = window.setInterval(() => setNowUnix(Math.floor(Date.now() / 1000)), 1000)
        return () => window.clearInterval(timer)
    }, [endTimeUnix, isEnded, isOwner, rewardWithdrawalProtection.isAvailable])
    useEffect(() => {
        if (!isOwner || !isEnded || !endTimeUnix || rewardWithdrawalProtection.remainingSeconds === 0) return
        if ((rewardClaimAudit.status || 'idle') !== 'idle') return
        controller.checkRewardClaimAudit?.()
    }, [controller, endTimeUnix, isEnded, isOwner, rewardClaimAudit.status, rewardWithdrawalProtection.remainingSeconds])

    const confirmNftSelection = async (tokenId) => {
        if (selectionMode === 'stake') {
            setSelectionMode(null)
            return controller.openNftStake?.(tokenId)
        }
        if (selectionMode === 'unstake') {
            return controller.unstakeNft?.(tokenId)
        }
        return false
    }

    if (!essentialDataReady) {
        const hasReadError = Boolean(poolDataError) || initialLoadTimedOut
        const readTimedOut = initialLoadTimedOut || poolDataErrorKind === 'timeout'
        return <main className="token-staking-console nft-staking-console">
            <section className="console-initial-loading" role="status" aria-live="polite" aria-label={t('正在加载矿池')}>
                <div className="console-initial-loading__signal" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                </div>
                <span className="console-eyebrow">{hasReadError ? t(readTimedOut ? '链上读取超时' : '链上读取失败') : t('正在加载矿池')}</span>
                <h1>{hasReadError ? t('暂时无法读取矿池数据') : t('正在同步链上数据')}</h1>
                <p>{hasReadError ? t(readTimedOut ? '当前 RPC 响应较慢或不可用，请重试。' : '矿池合约返回异常，请检查合约地址、网络或稍后重试。') : t('正在读取矿池、资产与钱包信息，请稍候。')}</p>
                {hasReadError
                    ? <button type="button" className="console-button console-button--primary" onClick={() => {
                        setInitialLoadAttempt((value) => value + 1)
                        controller.fetchData?.()
                    }}>{t('重新加载')}</button>
                    : <div className="console-initial-loading__progress" aria-hidden="true"><span /></div>}
            </section>
        </main>
    }

    return <main className="token-staking-console nft-staking-console">
        <section className="console-pool-cycle" aria-label={t('矿池周期')}>
            <div><span>{t('开始时间')}</span><strong>{formatPoolDate(poolInfo.startTime)}</strong></div>
            <div><span>{t('结束时间')}</span><strong>{formatPoolDate(poolInfo.endTime)}</strong></div>
            <div className="console-pool-cycle__countdown"><span>{countdownLabel}</span><strong>{countdownLoading ? '--:--:--:--' : countdown.ct || '--:--:--:--'}</strong></div>
            {isOwner && <button type="button" className="console-pool-cycle__admin" aria-label={t('查看管理员面板')} onClick={() => document.getElementById('nft-admin-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                <i className="fa fa-shield-alt" aria-hidden="true" />
                <span>{t('管理员面板')}</span>
                <i className="fa fa-arrow-down" aria-hidden="true" />
            </button>}
        </section>
        <div className="token-staking-console__layout">
            <PoolOverview state={state} t={t} getLockTimeString={controller.getLockTimeString?.bind(controller) || (() => '-')} onCopy={(value) => controller.copyValue?.(value)} />
            <section className="console-section console-operation-panel">
                <div className="console-section__heading"><div><span className="console-eyebrow">{t('我的仓位')}</span><h2>{account ? t('NFT 质押操作') : t('连接钱包')}</h2></div></div>
                {account ? <><div className="console-position-grid"><div><span>{t('钱包 NFT')}</span><strong>{walletNftsLoading ? t('加载中') : availableNfts.length}</strong></div><div><span>{t('我的质押数量')}</span><strong>{poolLoaded ? formatIntegerAmount(poolInfo.userStakedAmount || stakedNftIds.length) : t('加载中')}</strong></div><div className="is-reward"><span>{t('待领取奖励')}</span><strong>{state.rewardTokenInfoLoading ? t('加载中') : `${formatTokenAmount(poolInfo.pendingReward, rewardTokenInfo.decimals)} ${rewardTokenInfo.symbol || ''}`}</strong></div><div><span>{t('质押资产')}</span><strong>{state.stakeTokenInfoLoading ? t('加载中') : stakeTokenInfo.symbol || 'NFT'}</strong></div></div><div className={`nft-console-actions ${poolInfo.allowMergencyWithdraw ? 'has-emergency' : ''}`}><button type="button" className="console-button console-button--primary" onClick={() => setSelectionMode('stake')} disabled={isEnded}>{t('质押 NFT')}</button><button type="button" className="console-button console-button--success" onClick={() => controller.claimReward?.()} disabled={Boolean(state.reawardLoading)}>{t('领取奖励')}</button><button type="button" className="console-button" onClick={() => setSelectionMode('unstake')} disabled={stakedNftsLoading || stakedNftIds.length === 0}>{t('解除质押')}</button>{poolInfo.allowMergencyWithdraw && <button type="button" className="console-button console-button--danger" onClick={() => controller.emergencyWithdraw?.()} disabled={Boolean(state.emergencyWithdrawLoading)}>{t('紧急撤离')}</button>}</div></> : <div className="console-empty-state"><i className="fa fa-wallet" /><p>{t('连接钱包后可查看 NFT、质押和领取奖励。')}</p><span>{t('请使用页面右上角的钱包按钮连接。')}</span></div>}
            </section>
        </div>
        {account && <section className="console-section nft-console-staked"><div className="console-section__heading"><div><span className="console-eyebrow">{t('我的仓位')}</span><h2>{t('已质押 NFT')}</h2></div><span className="console-count">{stakedNftsLoading ? '-' : stakedNftIds.length}</span></div>{stakedNftsLoading ? <div className="console-empty-list">{t('加载中')}</div> : stakedNftIds.length > 0 ? <NftPagedList nfts={stakedNftIds.map((id) => ({ id: tokenIdOf(id) }))} metadataById={nftMetadataById} onVisibleTokenIds={loadVisibleNftMetadata} label={t('已质押 NFT')} resetKey={`${account}:${state.chainId}:staked`} t={t} /> : <div className="console-empty-list">{t('暂无已质押 NFT')}</div>}</section>}
        <ReferralPanel state={state} t={t} shareLink={account ? controller.generateShareLink?.() : ''} onCopy={(value) => controller.copyValue?.(value, t('复制成功'))} onPageChange={(page) => controller.getInvitorList?.(page)} />
        {state.ipfsInfo?.stakedRule && <section className="console-section console-rule-section"><span className="console-eyebrow">{t('质押规则')}</span><p>{state.ipfsInfo.stakedRule}</p></section>}
        {Array.isArray(state.ipfsInfo?.extra) && state.ipfsInfo.extra.map((item, index) => <section className="console-section console-rule-section" key={`${item.name || 'info'}-${index}`}><span className="console-eyebrow">{item.name}</span><p>{item.content}</p></section>)}
        {isOwner && <section id="nft-admin-panel" className="console-section console-admin-preview"><div className="console-section__heading"><div><span className="console-eyebrow">{t('管理员控制')}</span><h2>{t('矿池数据')}</h2></div></div>
            <div className="console-admin-groups">
                <section><h3>{t('项目资料')}</h3><button type="button" className="console-button" onClick={() => controller.openProjectInfo?.()}>{t('设置项目信息')}</button></section>
                <section><h3>{t('推广与访问')}</h3><button type="button" className="console-button" disabled={poolInfo.inviterSupported !== true} onClick={() => controller.openInviteSettings?.()}>{poolInfo.inviterSupported === false ? t('当前矿池不支持推广邀请') : poolInfo.inviterSupported === true ? (poolInfo.enableInvitor ? t('设置推广算力等级') : t('开启推广邀请')) : t('正在检测推广功能')}</button>{poolInfo.inviterSupported === true && poolInfo.enableInvitor && <button type="button" className="console-button" onClick={() => controller.openWhitelistSettings?.()}>{t('设置白名单')}</button>}<button type="button" className="console-button" onClick={() => controller.copyPoolAddress?.()}>{t('复制矿池地址')}</button>{controller.canBindDomain?.() && <button type="button" className="console-button" onClick={() => controller.showDomainBindingInfo?.()}>{t('绑定独立域名')}</button>}</section>
                <section><h3>{t('矿池控制')}</h3><button type="button" className="console-button" onClick={() => askDangerAction({ kind: 'emergency-withdraw', enable: !poolInfo.allowMergencyWithdraw, label: poolInfo.allowMergencyWithdraw ? t('禁止紧急撤离') : t('允许紧急撤离'), description: t('此操作会立即改变所有用户的紧急撤离权限。'), run: () => controller.enableForceWithdraw?.({ errorMode: 'local' }) })} disabled={Boolean(state.enableForceWithdrawLoading)}>{state.enableForceWithdrawLoading && <i className="fa fa-spinner loading-css" />} {poolInfo.allowMergencyWithdraw ? t('禁止紧急撤离') : t('允许紧急撤离')}</button>{!isEnded && <button type="button" className="console-button console-button--danger" onClick={() => askDangerAction({ label: t('立即终止矿池'), description: t('终止后矿池将不再继续发放奖励。'), run: () => controller.stopRewardLogic?.({ errorMode: 'local' }) })} disabled={Boolean(state.stopRewardLoading)}>{state.stopRewardLoading && <i className="fa fa-spinner loading-css" />} {t('立即终止矿池')}</button>}</section>
            </div>
            <RewardWithdrawalPanel
                isEnded={isEnded}
                loading={Boolean(state.stopRewardLoading)}
                poolInfo={poolInfo}
                protection={rewardWithdrawalProtection}
                rewardClaimAudit={rewardClaimAudit}
                rewardSymbol={rewardTokenInfo.symbol}
                t={t}
                onWithdraw={() => askDangerAction({ label: t('提取剩余奖励代币'), description: t('该操作会将矿池剩余奖励资产转出。'), run: () => controller.withdrawRemindRewardTokens?.({ errorMode: 'local' }) })}
            />
            <div className="console-admin-data"><h3>{t('数据面板')}</h3><div className="console-admin-metrics"><div><span>{t('质押用户数')}</span><strong>{adminInfo.userCount || 0}</strong></div><div><span>{t('质押总量')}</span><strong>{formatIntegerAmount(adminInfo.totalStakeAmount)}</strong></div></div><DataTable title={t('质押用户列表')} rows={state.stakeUserList || []} total={adminInfo.userCount} perPage={state.perPage} stakeIsInteger rewardDecimals={rewardTokenInfo.decimals} onPageChange={(page) => controller.getStakeListForAdmin?.(page)} onCopy={(value) => controller.copyValue?.(value, t('复制成功'))} t={t} /></div>
        </section>}
        <NftSelectionDialog
            show={Boolean(selectionMode)}
            mode={selectionMode || 'stake'}
            nfts={selectionMode === 'unstake' ? stakedNftIds.map((id) => ({ id: tokenIdOf(id) })) : availableNfts}
            metadataById={nftMetadataById}
            loading={selectionMode === 'unstake' ? stakedNftsLoading : walletNftsLoading}
            onClose={() => setSelectionMode(null)}
            onConfirm={confirmNftSelection}
            onRefresh={() => controller.loadWalletNfts?.()}
            onAddManual={(tokenId) => controller.addManualNft?.(tokenId)}
            onVisibleTokenIds={loadVisibleNftMetadata}
            t={t}
        />
        <ConsoleDialog show={Boolean(dangerAction)} onClose={() => !dangerLoading && setDangerAction(null)} closeLabel={t('关闭')} title={dangerAction?.label || t('危险操作确认')} description={dangerAction?.description} icon="fa-exclamation-triangle" tone="danger" footer={<><button type="button" className="console-button" disabled={dangerLoading} aria-label={`${t('取消')}${dangerAction?.label || ''}`} onClick={() => setDangerAction(null)}>{t('取消')}</button><button type="button" className="console-button console-button--danger" disabled={dangerLoading} aria-label={`${t('确认')}${dangerAction?.label || ''}`} onClick={confirmDangerAction}>{dangerLoading ? t('处理中...') : t('确认')}</button></>}>
            {dangerAction?.kind === 'emergency-withdraw' && <div className="console-emergency-explainer">
                <div className="console-emergency-explainer__intro">
                    <i className="fa fa-life-ring" aria-hidden="true" />
                    <div><strong>{t('什么是紧急撤离？')}</strong><p>{t('紧急撤离允许用户立即取回全部已质押 NFT。')}</p></div>
                </div>
                <ul>
                    <li><i className="fa fa-coins" aria-hidden="true" /><span>{t('使用紧急撤离不会获得本次尚未领取的奖励。')}</span></li>
                    <li><i className="fa fa-wallet" aria-hidden="true" /><span>{t('此设置只改变紧急出口权限，不会自动移动任何用户资产。')}</span></li>
                    <li><i className="fa fa-clock" aria-hidden="true" /><span>{t('关闭后，用户仍需按正常解锁规则逐枚解除质押。')}</span></li>
                </ul>
                <div className={`console-emergency-impact ${dangerAction.enable ? 'is-enable' : 'is-disable'}`}>
                    <i className={`fa ${dangerAction.enable ? 'fa-toggle-on' : 'fa-toggle-off'}`} aria-hidden="true" />
                    <span>{dangerAction.enable ? t('开启后，所有质押用户都可以使用紧急撤离取回 NFT。') : t('关闭后，用户仍需按正常解锁规则逐枚解除质押。')}</span>
                </div>
            </div>}
            {dangerError && <p className="console-form-error" role="alert">{dangerError}</p>}
        </ConsoleDialog>
        <ConsoleFooter
            ipfsInfo={state.ipfsInfo}
            projectName={state.ipfsInfo?.project_name || state.stakeTokenInfo?.name || state.stakeTokenInfo?.symbol}
            t={t}
        />
    </main>
}
